import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Calculator, ChevronRight, Phone, ArrowRight, DollarSign, Percent, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import Header from '../components/Header';
import Footer from '../components/Footer';
import InquiryForm from '../components/InquiryForm';
import SEOHead from '../components/SEOHead';

const fmt = (v) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(v);
const fmt2 = (v) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v);

const SliderField = ({ label, desc, value, onChange, min, max, step, suffix, prefix, testId }) => {
  const pct = ((value - min) / (max - min)) * 100;
  return (
    <div className="space-y-2">
      <h3 className="text-base font-bold text-blue-600 text-center">{label}</h3>
      {desc && <p className="text-xs text-gray-500 text-center">{desc}</p>}
      <div className="text-center text-sm font-semibold text-gray-800 mb-1">{prefix}{value}{suffix}</div>
      <input data-testid={testId} type="range" min={min} max={max} step={step} value={value}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        className="w-full h-2 rounded-full appearance-none cursor-pointer"
        style={{ background: `linear-gradient(to right, #2563EB ${pct}%, #e5e7eb ${pct}%)` }} />
      <div className="flex justify-between text-xs text-gray-400">
        <span>{prefix}{min}{suffix}</span><span>{prefix}{max}{suffix}</span>
      </div>
    </div>
  );
};

const RadioField = ({ label, options, value, onChange, testId }) => (
  <div className="space-y-2">
    <h3 className="text-base font-bold text-blue-600 text-center">{label}</h3>
    <div className="flex flex-wrap justify-center gap-3">
      {options.map((opt) => (
        <label key={opt.value} className="flex items-center gap-1.5 cursor-pointer">
          <input type="radio" name={testId} value={opt.value} checked={value === opt.value}
            onChange={() => onChange(opt.value)}
            className="w-4 h-4 text-blue-600 accent-blue-600" />
          <span className="text-sm text-gray-700">{opt.label}</span>
        </label>
      ))}
    </div>
  </div>
);

const OutputCard = ({ label, value, large }) => (
  <div className={`bg-slate-900 rounded-xl overflow-hidden ${large ? 'col-span-full' : ''}`}>
    <div className="px-4 py-2 text-center">
      <span className="text-xs font-semibold text-blue-300 uppercase tracking-wider">{label}</span>
    </div>
    <div className="bg-white mx-1 mb-1 rounded-lg px-4 py-3 text-center">
      <span className={`font-bold text-gray-900 ${large ? 'text-2xl' : 'text-base'}`}>{value}</span>
    </div>
  </div>
);

const DealCalculatorPage = () => {
  const [isFormOpen, setIsFormOpen] = useState(false);

  const [purchasePrice, setPurchasePrice] = useState(125000);
  const [rehabBudget, setRehabBudget] = useState(20000);
  const [arv, setArv] = useState(180000);
  const [ltv, setLtv] = useState(80);
  const [turnaround, setTurnaround] = useState(6);
  const [interestRate, setInterestRate] = useState(12);
  const [points, setPoints] = useState(2);
  const [brokerFeeSale, setBrokerFeeSale] = useState(0);
  const [mortgageBrokerFee, setMortgageBrokerFee] = useState(0);
  const [transferTax, setTransferTax] = useState(1.5);
  const [propertyTaxYearly, setPropertyTaxYearly] = useState(4500);

  const results = useMemo(() => {
    const loanAmount = purchasePrice * (ltv / 100);
    const purchaseAndRehab = purchasePrice + rehabBudget;
    const loanToDealCost = purchaseAndRehab > 0 ? (loanAmount / purchaseAndRehab) * 100 : 0;
    const monthlyInterest = loanAmount * (interestRate / 100 / 12);
    const totalInterest = monthlyInterest * turnaround;
    const pointsCost = loanAmount * (points / 100);
    const brokerFeeSaleAmt = arv * (brokerFeeSale / 100);
    const mortgageBrokerFeeAmt = loanAmount * (mortgageBrokerFee / 100);
    const propInsurance = loanAmount * 0.0045;
    const titleInsurance = arv * 0.005 + 75;
    const transferTaxAmt = purchasePrice * (transferTax / 100);
    const propTaxProrated = propertyTaxYearly * (turnaround / 12);

    const totalProjectCost = purchaseAndRehab + totalInterest + pointsCost + brokerFeeSaleAmt +
      mortgageBrokerFeeAmt + propInsurance + titleInsurance + transferTaxAmt + propTaxProrated;

    const netSalesPrice = arv - brokerFeeSaleAmt;
    const netProfit = netSalesPrice - totalProjectCost;
    const returnOnCost = totalProjectCost > 0 ? (netProfit / totalProjectCost) * 100 : 0;

    return {
      loanAmount, purchaseAndRehab, loanToDealCost, monthlyInterest, totalInterest,
      pointsCost, brokerFeeSaleAmt, mortgageBrokerFeeAmt, propInsurance, titleInsurance,
      transferTaxAmt, totalProjectCost, netProfit, returnOnCost, propTaxProrated,
    };
  }, [purchasePrice, rehabBudget, arv, ltv, turnaround, interestRate, points, brokerFeeSale, mortgageBrokerFee, transferTax, propertyTaxYearly]);

  return (
    <div className="min-h-screen" data-testid="deal-calculator-page">
      <Helmet>
        <title>Fix and Flip Loan Calculator | Hard Money Deal Analyzer | Close Now Lending</title>
        <meta name="description" content="Free fix and flip loan calculator for real estate investors. Analyze hard money deals, calculate ROI, estimate profits, and determine financing needs for your next flip." />
        <meta name="keywords" content="fix and flip calculator, hard money calculator, house flip calculator, real estate deal analyzer, flip profit calculator, rehab loan calculator" />
        <meta property="og:title" content="Fix & Flip Deal Calculator | Close Now Lending" />
        <meta property="og:description" content="Analyze your next flip deal. Calculate ROI, estimate costs, and determine financing needs with our free calculator." />
        <meta property="og:url" content="https://closenowlending.com/deal-calculator" />
        <link rel="canonical" href="https://closenowlending.com/deal-calculator" />
      </Helmet>
      <Header />

      {/* Hero */}
      <section className="relative bg-slate-900 pt-28 pb-16">
        <div className="container mx-auto px-4">
          <nav className="flex items-center gap-2 text-sm text-gray-400 mb-6">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <ChevronRight className="h-4 w-4" />
            <span className="text-blue-400">Fix and Flip Loan Calculator</span>
          </nav>
          <div className="flex items-center gap-4 mb-4">
            <div className="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center">
              <Calculator className="h-6 w-6 text-white" />
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white">
              Fix and Flip Loan Calculator
            </h1>
          </div>
          <p className="text-lg text-gray-300 max-w-2xl">
            Use this calculator to estimate costs and returns for your fix and flip project. Enter your deal economics below to see projected outcomes.
          </p>
        </div>
      </section>

      {/* Calculator */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4 max-w-5xl">

          {/* Inputs Header */}
          <div className="bg-blue-600 rounded-t-2xl px-6 py-4 text-center">
            <h2 className="text-xl font-bold text-white">Inputs</h2>
            <p className="text-blue-100 text-sm">Use the following fields to enter your estimated deal economics. These fields will be used to calculate the outputs that follow.</p>
          </div>

          <div className="bg-white rounded-b-2xl border border-gray-200 border-t-0 p-6 md:p-8 space-y-8 mb-12">
            {/* Row 1: Purchase Price, Rehab Budget, ARV */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <h3 className="text-base font-bold text-blue-600 text-center">Purchase Price</h3>
                <p className="text-xs text-gray-500 text-center mb-2">The price you are buying the property</p>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">$</span>
                  <Input data-testid="input-purchase-price" type="number" value={purchasePrice}
                    onChange={(e) => setPurchasePrice(Number(e.target.value) || 0)} className="pl-7 text-center" />
                </div>
              </div>
              <div>
                <h3 className="text-base font-bold text-blue-600 text-center">Rehab Budget</h3>
                <p className="text-xs text-gray-500 text-center mb-2">The rehab money needed for improvements</p>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">$</span>
                  <Input data-testid="input-rehab-budget" type="number" value={rehabBudget}
                    onChange={(e) => setRehabBudget(Number(e.target.value) || 0)} className="pl-7 text-center" />
                </div>
              </div>
              <div>
                <h3 className="text-base font-bold text-blue-600 text-center">After Repair Value (ARV)</h3>
                <p className="text-xs text-gray-500 text-center mb-2">Value of the property after repairs are completed</p>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">$</span>
                  <Input data-testid="input-arv" type="number" value={arv}
                    onChange={(e) => setArv(Number(e.target.value) || 0)} className="pl-7 text-center" />
                </div>
              </div>
            </div>

            {/* Row 2: LTV slider, Turnaround slider */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <SliderField label="Initial Loan Amount (LTV)" desc="Initial Loan Amount as % of Purchase Price"
                value={ltv} onChange={setLtv} min={60} max={90} step={1} prefix="" suffix=" %" testId="input-ltv" />
              <SliderField label="Turnaround Time (Close To Sale)" desc="Measured from purchase of property until sold"
                value={turnaround} onChange={setTurnaround} min={0} max={12} step={1} prefix="" suffix=" months" testId="input-turnaround" />
            </div>

            {/* Row 3: Interest Rate slider */}
            <div className="max-w-md mx-auto">
              <SliderField label="Interest Rate (Annually)" desc="The interest rate being charged on your loan"
                value={interestRate} onChange={setInterestRate} min={10} max={15} step={0.25} prefix="" suffix=" %" testId="input-interest" />
            </div>

            {/* Row 4: Points, RE Broker Fee, Mortgage Broker Fee */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <RadioField label="Points (Loan Origination Fee)" testId="points"
                options={[{label:'1%',value:1},{label:'2%',value:2},{label:'3%',value:3},{label:'4%',value:4},{label:'5%',value:5}]}
                value={points} onChange={setPoints} />
              <RadioField label="Real Estate Broker Fee (On Sale)" testId="broker-sale"
                options={[{label:'0%',value:0},{label:'2%',value:2},{label:'3%',value:3},{label:'4%',value:4},{label:'5%',value:5},{label:'6%',value:6}]}
                value={brokerFeeSale} onChange={setBrokerFeeSale} />
              <RadioField label="Mortgage Broker Fee" testId="mortgage-broker"
                options={[{label:'N/A',value:0},{label:'1%',value:1},{label:'1.5%',value:1.5},{label:'2%',value:2},{label:'2.5%',value:2.5},{label:'3%',value:3}]}
                value={mortgageBrokerFee} onChange={setMortgageBrokerFee} />
            </div>

            {/* Row 5: Transfer Tax slider */}
            <div className="max-w-md mx-auto">
              <SliderField label="State, Recording, & Transfer Tax (%)" desc=""
                value={transferTax} onChange={setTransferTax} min={0} max={4} step={0.1} prefix="" suffix=" %" testId="input-transfer-tax" />
            </div>

            {/* Row 6: Property Taxes */}
            <div className="max-w-sm mx-auto">
              <h3 className="text-base font-bold text-blue-600 text-center">Property Taxes</h3>
              <p className="text-xs text-gray-500 text-center mb-2">Yearly taxes on the subject property</p>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">$</span>
                <Input data-testid="input-property-tax" type="number" value={propertyTaxYearly}
                  onChange={(e) => setPropertyTaxYearly(Number(e.target.value) || 0)} className="pl-7 text-center" />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">Per Year</span>
              </div>
            </div>
          </div>

          {/* Outputs Header */}
          <div className="bg-blue-500 rounded-t-2xl px-6 py-4 text-center">
            <h2 className="text-xl font-bold text-white">Outputs</h2>
            <p className="text-blue-100 text-sm">The following fields are calculated using the information you have entered above. Please keep in mind that these calculations are reasonably accurate <em>but they are just estimates.</em></p>
          </div>

          <div className="bg-white rounded-b-2xl border border-gray-200 border-t-0 p-6 md:p-8 space-y-6 mb-12">
            {/* Top Results */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <OutputCard label="Total Project Cost" value={fmt(results.totalProjectCost)} />
              <OutputCard label="Return On Project Cost" value={`${results.returnOnCost.toFixed(1)} %`} />
            </div>
            <OutputCard label="Net Profit (Total Project Cost Less Net Sales Price)" value={fmt(results.netProfit)} large />

            {/* Detail Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <OutputCard label="Purchase And Rehab Cost" value={fmt(results.purchaseAndRehab)} />
              <OutputCard label="Loan Amount" value={fmt(results.loanAmount)} />
              <OutputCard label="Loan To Deal Cost" value={`${results.loanToDealCost.toFixed(0)} %`} />
              <OutputCard label="Monthly Interest Payment" value={fmt2(results.monthlyInterest)} />
              <OutputCard label="Points Cost" value={fmt2(results.pointsCost)} />
              <OutputCard label="Total Interest Cost" value={fmt2(results.totalInterest)} />
              <OutputCard label="Real Estate Broker Fee (Sale)" value={fmt2(results.brokerFeeSaleAmt)} />
              <OutputCard label="Mortgage Broker Fee" value={fmt2(results.mortgageBrokerFeeAmt)} />
              <OutputCard label="Approx. Property Insurance" value={fmt2(results.propInsurance)} />
              <OutputCard label="Approx. Title Insurance" value={fmt2(results.titleInsurance)} />
              <OutputCard label="State, Recording, Transfer Tax" value={fmt2(results.transferTaxAmt)} />
              <OutputCard label="Property Tax (Prorated)" value={fmt2(results.propTaxProrated)} />
            </div>
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-16 bg-slate-900">
        <div className="container mx-auto px-4 text-center max-w-3xl">
          <h2 className="text-3xl font-bold text-white mb-4">Ready to Fund Your Next Deal?</h2>
          <p className="text-gray-300 mb-8 text-lg">
            Our lending team can help you analyze your fix and flip deal and find the right financing. Get in touch today for a personalized consultation.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button data-testid="deal-calc-apply-btn" onClick={() => setIsFormOpen(true)} size="lg"
              className="bg-blue-600 hover:bg-blue-700 text-white px-8">
              Get Started <ArrowRight className="h-5 w-5 ml-2" />
            </Button>
            <a href="tel:903-476-2937">
              <Button size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/10 px-8">
                <Phone className="h-5 w-5 mr-2" /> Call 903-476-2937
              </Button>
            </a>
          </div>
        </div>
      </section>

      <Footer />
      <InquiryForm isOpen={isFormOpen} onClose={() => setIsFormOpen(false)} loanType="Fix and Flip" />
    </div>
  );
};

export default DealCalculatorPage;
